<template>
  <div style="max-width: 90%; margin: auto" class="py-10">
    <!-- Header -->
    <div class="d-flex justify-space-between align-center mb-8">
      <div>
        <p class="page-subtitle mb-1">{{ greeting }}</p>
        <h1 class="page-title">Willkommen zurück!</h1>
      </div>
      <button class="refresh-btn" :disabled="loading" @click="loadStatistics">
        <v-progress-circular v-if="loading" indeterminate size="14" width="2" color="#0a1628" />
        <v-icon v-else size="16" color="#0a1628">mdi-refresh</v-icon>
        <span>Aktualisieren</span>
      </button>
    </div>

    <!-- Loading skeletons -->
    <div v-if="loading && !statistics.summary" class="loading-state">
      <div class="skel-card mb-6">
        <div class="d-flex">
          <div v-for="n in 4" :key="n" style="flex: 1; padding: 0 16px">
            <div class="skel" style="width: 70px; height: 10px; margin-bottom: 10px"></div>
            <div class="skel" style="width: 120px; height: 22px"></div>
          </div>
        </div>
      </div>
      <div class="d-flex mb-6" style="gap: 20px">
        <div style="flex: 2"><div class="skel-card" style="height: 340px"></div></div>
        <div style="flex: 1"><div class="skel-card" style="height: 340px"></div></div>
      </div>
      <div class="skel-card" style="height: 240px"></div>
    </div>

    <template v-else-if="statistics.summary">
      <!-- KPI Strip -->
      <div class="metric-strip mb-8">
        <div class="metric-item metric-border">
          <div class="metric-icon" style="background: rgba(91,91,214,0.08)">
            <v-icon size="18" color="#5B5BD6">mdi-currency-eur</v-icon>
          </div>
          <div>
            <div class="metric-label">Gesamtumsatz</div>
            <div class="metric-value">{{ formatCurrency(statistics.summary.totalRevenue) }}</div>
            <div class="metric-sub">
              <span :class="statistics.growth.revenueGrowth >= 0 ? 'trend-up' : 'trend-down'">
                <v-icon size="11">{{ statistics.growth.revenueGrowth >= 0 ? 'mdi-trending-up' : 'mdi-trending-down' }}</v-icon>
                {{ Math.abs(statistics.growth.revenueGrowth) }}%
              </span>
              zum Vormonat
            </div>
          </div>
        </div>

        <div class="metric-item metric-border">
          <div class="metric-icon" style="background: rgba(46,125,50,0.08)">
            <v-icon size="18" color="#2E7D32">mdi-cart-check</v-icon>
          </div>
          <div>
            <div class="metric-label">Verkaufte Sections</div>
            <div class="metric-value">{{ statistics.summary.totalPurchases.toLocaleString('de-DE') }}</div>
            <div class="metric-sub">
              <span :class="statistics.growth.purchaseGrowth >= 0 ? 'trend-up' : 'trend-down'">
                <v-icon size="11">{{ statistics.growth.purchaseGrowth >= 0 ? 'mdi-trending-up' : 'mdi-trending-down' }}</v-icon>
                {{ Math.abs(statistics.growth.purchaseGrowth) }}%
              </span>
              zum Vormonat
            </div>
          </div>
        </div>

        <div class="metric-item metric-border">
          <div class="metric-icon" style="background: rgba(124,77,255,0.08)">
            <v-icon size="18" color="#7C4DFF">mdi-account-group</v-icon>
          </div>
          <div>
            <div class="metric-label">Unique Kunden</div>
            <div class="metric-value">{{ statistics.summary.uniqueCustomers.toLocaleString('de-DE') }}</div>
            <div class="metric-sub">aktive Shops</div>
          </div>
        </div>

        <div class="metric-item">
          <div class="metric-icon" style="background: rgba(255,152,0,0.08)">
            <v-icon size="18" color="#FB8C00">mdi-chart-line</v-icon>
          </div>
          <div>
            <div class="metric-label">Ø Verkaufspreis</div>
            <div class="metric-value">{{ formatCurrency(statistics.summary.averagePrice) }}</div>
            <div class="metric-sub">pro Section</div>
          </div>
        </div>
      </div>

      <!-- Chart + Comparison -->
      <div class="grid-split mb-6">
        <div class="card-modern">
          <div class="d-flex justify-space-between align-center mb-5">
            <div>
              <div class="card-title">Verkaufsübersicht</div>
              <div class="card-desc">Letzte 30 Tage</div>
            </div>
            <div class="d-flex align-center" style="gap: 16px">
              <div class="legend-dot-label"><span class="legend-dot" style="background: #2E7D32"></span>Verkäufe</div>
              <div class="legend-dot-label"><span class="legend-dot" style="background: #5B5BD6"></span>Umsatz</div>
            </div>
          </div>

          <div v-if="chartData" class="chart-wrapper">
            <svg
              class="modern-chart"
              viewBox="0 0 100 60"
              preserveAspectRatio="none"
              @mousemove="handleChartHover"
              @mouseleave="hoveredIndex = null"
            >
              <line
                v-for="i in 5"
                :key="`grid-${i}`"
                :y1="(i - 1) * 15"
                :y2="(i - 1) * 15"
                x1="0"
                x2="100"
                stroke="#f0f2f5"
                stroke-width="0.2"
              />
              <defs>
                <linearGradient id="salesGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" style="stop-color: #2E7D32; stop-opacity: 0.18" />
                  <stop offset="100%" style="stop-color: #2E7D32; stop-opacity: 0" />
                </linearGradient>
                <linearGradient id="revenueGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" style="stop-color: #5B5BD6; stop-opacity: 0.18" />
                  <stop offset="100%" style="stop-color: #5B5BD6; stop-opacity: 0" />
                </linearGradient>
              </defs>
              <path :d="getAreaPath('sales')" fill="url(#salesGradient)" />
              <path :d="getAreaPath('revenue')" fill="url(#revenueGradient)" />
              <path :d="getLinePath('sales')" fill="none" stroke="#2E7D32" stroke-width="0.6" />
              <path :d="getLinePath('revenue')" fill="none" stroke="#5B5BD6" stroke-width="0.6" />
              <circle
                v-for="(value, index) in chartData.sales"
                :key="`sales-point-${index}`"
                :cx="getChartX(index)"
                :cy="getChartY(value, chartData.maxSales)"
                :r="hoveredIndex === index ? '1.2' : '0.5'"
                fill="#2E7D32"
              />
              <circle
                v-for="(value, index) in chartData.revenue"
                :key="`revenue-point-${index}`"
                :cx="getChartX(index)"
                :cy="getChartY(value, chartData.maxRevenue)"
                :r="hoveredIndex === index ? '1.2' : '0.5'"
                fill="#5B5BD6"
              />
            </svg>

            <div class="d-flex mt-5" style="gap: 12px">
              <div class="chart-stat-card">
                <div class="chart-stat-label">Verkäufe Gesamt</div>
                <div class="chart-stat-value">{{ chartData.sales.reduce((a, b) => a + b, 0) }}</div>
              </div>
              <div class="chart-stat-card">
                <div class="chart-stat-label">Umsatz Gesamt</div>
                <div class="chart-stat-value">{{ formatCurrency(chartData.revenue.reduce((a, b) => a + b, 0)) }}</div>
              </div>
            </div>
          </div>
        </div>

        <div class="d-flex flex-column" style="gap: 16px">
          <div class="card-modern" style="flex: 1">
            <div class="card-title mb-1">Monatlicher Vergleich</div>
            <div class="card-desc mb-4">Aktueller Monat vs. Vormonat</div>

            <div class="prognose-item mb-2">
              <div style="flex: 1">
                <div class="prognose-label">Aktueller Monat</div>
                <div class="mt-1" style="font-size: 18px; font-weight: 700; color: #0a1628">
                  {{ statistics.growth.currentMonth.purchases }} <span class="month-sub">Verkäufe</span>
                </div>
                <div class="month-revenue">{{ formatCurrency(statistics.growth.currentMonth.revenue) }}</div>
              </div>
              <div
                class="month-badge"
                :class="statistics.growth.purchaseGrowth >= 0 ? 'badge-up' : 'badge-down'"
              >
                <v-icon size="11">{{ statistics.growth.purchaseGrowth >= 0 ? 'mdi-arrow-up' : 'mdi-arrow-down' }}</v-icon>
                {{ Math.abs(statistics.growth.purchaseGrowth) }}%
              </div>
            </div>

            <div class="prognose-item" style="opacity: 0.7">
              <div style="flex: 1">
                <div class="prognose-label">Vormonat</div>
                <div class="mt-1" style="font-size: 16px; font-weight: 600; color: #555">
                  {{ statistics.growth.previousMonth.purchases }} <span class="month-sub">Verkäufe</span>
                </div>
                <div class="month-revenue" style="color: #888">
                  {{ formatCurrency(statistics.growth.previousMonth.revenue) }}
                </div>
              </div>
            </div>
          </div>

          <div class="card-modern" style="flex: 1">
            <div class="card-title mb-4">Verkäufe nach Typ</div>
            <div class="type-row">
              <div class="type-icon" style="background: rgba(255,152,0,0.1)">
                <v-icon size="16" color="#FB8C00">mdi-star</v-icon>
              </div>
              <div class="type-label">Premium Sections</div>
              <div class="type-value">{{ statistics.summary.premiumSales }}</div>
            </div>
            <div class="type-row">
              <div class="type-icon" style="background: #f0f2f5">
                <v-icon size="16" color="#888">mdi-gift-outline</v-icon>
              </div>
              <div class="type-label">Kostenlose Sections</div>
              <div class="type-value">{{ statistics.summary.freeSales }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Top Sections + Recent Activity -->
      <div class="grid-7-5">
        <div class="card-modern">
          <div class="card-title mb-4">Top 5 Sections</div>
          <div class="top-sections-list">
            <div
              v-for="(section, index) in statistics.topSections"
              :key="section.id"
              class="top-section-item"
            >
              <div class="section-rank-badge" :class="`rank-${index + 1}`">
                {{ index + 1 }}
              </div>
              <div class="section-details">
                <div class="section-title">{{ section.title }}</div>
                <div class="section-handle">{{ section.handle }}</div>
              </div>
              <div class="section-metrics">
                <div class="metric-box">
                  <div class="metric-box-value">{{ section.sales }}</div>
                  <div class="metric-box-label">Verkäufe</div>
                </div>
                <div class="metric-divider"></div>
                <div class="metric-box">
                  <div class="metric-box-value">{{ formatCurrency(section.revenue, true) }}</div>
                  <div class="metric-box-label">Umsatz</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="card-modern">
          <div class="card-title mb-4">Neueste Aktivität</div>
          <div class="activity-list">
            <div
              v-for="purchase in statistics.recentPurchases"
              :key="purchase.id"
              class="activity-item"
            >
              <div class="activity-icon">
                <v-icon size="14" color="#2E7D32">mdi-check-circle</v-icon>
              </div>
              <div class="activity-content">
                <div class="activity-title">{{ purchase.sectionTitle }}</div>
                <div class="activity-time">{{ formatDate(purchase.purchasedAt) }}</div>
              </div>
              <div class="activity-amount">{{ formatCurrency(purchase.amount) }}</div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'Dashboard',

  data: () => ({
    statistics: {},
    loading: false,
    hoveredIndex: null,
  }),

  computed: {
    greeting() {
      const hour = new Date().getHours();
      if (hour < 12) return 'Guten Morgen';
      if (hour < 18) return 'Guten Tag';
      return 'Guten Abend';
    },
    chartData() {
      if (!this.statistics.salesChart?.last30Days) return null;
      const data = this.statistics.salesChart.last30Days;
      const sales = data.map((d) => parseInt(d.sales) || 0);
      const revenue = data.map((d) => parseFloat(d.revenue) || 0);
      return {
        labels: data.map((d) =>
          new Date(d.date).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })
        ),
        sales,
        revenue,
        maxSales: Math.max(...sales, 1),
        maxRevenue: Math.max(...revenue, 1),
        points: data.length,
      };
    },
  },

  mounted() {
    this.loadStatistics();
  },

  methods: {
    async loadStatistics() {
      this.loading = true;
      try {
        const result = await axios.get('/api/dashboard/statistics');
        this.statistics = result.data;
      } catch (e) {
        console.error('Fehler beim Laden der Statistiken!', e);
      } finally {
        this.loading = false;
      }
    },
    getChartX(index) {
      if (!this.chartData) return 0;
      const spacing = 100 / (this.chartData.points - 1);
      return spacing * index;
    },
    getChartY(value, max, height = 60) {
      if (max === 0) return height;
      return height - (value / max) * height;
    },
    getLinePath(type) {
      if (!this.chartData) return '';
      const data = type === 'sales' ? this.chartData.sales : this.chartData.revenue;
      const max = type === 'sales' ? this.chartData.maxSales : this.chartData.maxRevenue;
      let path = `M ${this.getChartX(0)} ${this.getChartY(data[0], max)}`;
      for (let i = 1; i < data.length; i++) {
        path += ` L ${this.getChartX(i)} ${this.getChartY(data[i], max)}`;
      }
      return path;
    },
    getAreaPath(type) {
      if (!this.chartData) return '';
      const data = type === 'sales' ? this.chartData.sales : this.chartData.revenue;
      const max = type === 'sales' ? this.chartData.maxSales : this.chartData.maxRevenue;
      let path = `M ${this.getChartX(0)} 60`;
      path += ` L ${this.getChartX(0)} ${this.getChartY(data[0], max)}`;
      for (let i = 1; i < data.length; i++) {
        path += ` L ${this.getChartX(i)} ${this.getChartY(data[i], max)}`;
      }
      path += ` L ${this.getChartX(data.length - 1)} 60 Z`;
      return path;
    },
    handleChartHover(event) {
      if (!this.chartData) return;
      const rect = event.currentTarget.getBoundingClientRect();
      const x = ((event.clientX - rect.left) / rect.width) * 100;
      const index = Math.round((x / 100) * (this.chartData.points - 1));
      this.hoveredIndex = Math.max(0, Math.min(index, this.chartData.points - 1));
    },
    formatCurrency(value, short = false) {
      if (short && value >= 1000) return `€${(value / 1000).toFixed(1)}k`;
      return new Intl.NumberFormat('de-DE', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(value);
    },
    formatDate(dateString) {
      const date = new Date(dateString);
      const diff = new Date() - date;
      const minutes = Math.floor(diff / 60000);
      const hours = Math.floor(minutes / 60);
      const days = Math.floor(hours / 24);
      if (minutes < 60) return `vor ${minutes} Min`;
      if (hours < 24) return `vor ${hours} Std`;
      if (days < 7) return `vor ${days} Tagen`;
      return date.toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
  },
};
</script>

<style scoped>
.page-title { font-size: 26px; font-weight: 800; color: #0a1628; letter-spacing: -0.3px; }
.page-subtitle { font-size: 13px; color: #888; }

.refresh-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  background: white;
  border: 1px solid #eceef2;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  color: #0a1628;
  cursor: pointer;
  transition: border-color 0.15s;
}
.refresh-btn:hover:not(:disabled) { border-color: #0a1628; }
.refresh-btn:disabled { opacity: 0.6; cursor: not-allowed; }

/* Metric Strip */
.metric-strip { display: flex; background: white; border-radius: 16px; border: 1px solid #eceef2; padding: 20px 8px; }
.metric-item { flex: 1; display: flex; align-items: flex-start; gap: 14px; padding: 0 20px; min-width: 0; }
.metric-border { border-right: 1px solid #f0f0f0; }
.metric-icon { width: 40px; height: 40px; min-width: 40px; border-radius: 10px; display: flex; align-items: center; justify-content: center; margin-top: 2px; }
.metric-label { font-size: 11px; font-weight: 600; color: #999; text-transform: uppercase; letter-spacing: 0.3px; margin-bottom: 4px; }
.metric-value { font-size: 20px; font-weight: 700; color: #0a1628; font-variant-numeric: tabular-nums; margin-bottom: 4px; }
.metric-sub { font-size: 11px; color: #aaa; display: flex; align-items: center; gap: 4px; }
.trend-up { color: #2E7D32; font-weight: 700; display: inline-flex; align-items: center; gap: 2px; }
.trend-down { color: #D32F2F; font-weight: 700; display: inline-flex; align-items: center; gap: 2px; }

/* Cards */
.card-modern { background: white; border-radius: 16px; border: 1px solid #eceef2; padding: 24px; }
.card-title { font-size: 16px; font-weight: 700; color: #0a1628; }
.card-desc { font-size: 12px; color: #999; margin-top: 2px; }

/* Grids */
.grid-split { display: grid; grid-template-columns: 2fr 1fr; gap: 20px; }
.grid-7-5 { display: grid; grid-template-columns: 7fr 5fr; gap: 20px; }

/* Chart */
.chart-wrapper { width: 100%; position: relative; }
.modern-chart { width: 100%; height: 280px; background: #fafbfc; border-radius: 12px; padding: 16px; cursor: crosshair; border: 1px solid #f0f2f5; }
.legend-dot-label { display: flex; align-items: center; gap: 6px; font-size: 12px; color: #666; font-weight: 500; }
.legend-dot { width: 8px; height: 8px; border-radius: 50%; }

.chart-stat-card { flex: 1; padding: 14px 16px; background: #f8f9fb; border-radius: 10px; }
.chart-stat-label { font-size: 11px; font-weight: 600; color: #999; text-transform: uppercase; letter-spacing: 0.3px; margin-bottom: 6px; }
.chart-stat-value { font-size: 18px; font-weight: 700; color: #0a1628; font-variant-numeric: tabular-nums; }

/* Prognose / Monatsvergleich */
.prognose-item { display: flex; align-items: center; gap: 12px; padding: 14px 16px; background: #f8f9fb; border-radius: 10px; }
.prognose-label { font-size: 12px; color: #888; font-weight: 500; }
.month-sub { font-size: 12px; color: #888; font-weight: 500; }
.month-revenue { font-size: 13px; color: #2E7D32; font-weight: 600; margin-top: 2px; }
.month-badge { display: inline-flex; align-items: center; gap: 3px; padding: 4px 10px; border-radius: 6px; font-size: 11px; font-weight: 700; }
.badge-up { background: rgba(46,125,50,0.08); color: #2E7D32; }
.badge-down { background: rgba(211,47,47,0.08); color: #D32F2F; }

/* Verkäufe nach Typ */
.type-row { display: flex; align-items: center; gap: 12px; padding: 12px 0; border-bottom: 1px solid #f5f5f5; }
.type-row:last-child { border-bottom: none; }
.type-icon { width: 34px; height: 34px; min-width: 34px; border-radius: 9px; display: flex; align-items: center; justify-content: center; }
.type-label { flex: 1; font-size: 13px; font-weight: 600; color: #0a1628; }
.type-value { font-size: 18px; font-weight: 700; color: #0a1628; font-variant-numeric: tabular-nums; }

/* Top Sections */
.top-sections-list { display: flex; flex-direction: column; gap: 8px; }
.top-section-item { display: flex; align-items: center; gap: 14px; padding: 14px 16px; background: #fafbfc; border: 1px solid #eceef2; border-radius: 12px; transition: border-color 0.15s; }
.top-section-item:hover { border-color: #0a1628; }
.section-rank-badge { width: 32px; height: 32px; min-width: 32px; border-radius: 8px; display: flex; align-items: center; justify-content: center; font-size: 14px; font-weight: 800; color: white; flex-shrink: 0; }
.rank-1 { background: linear-gradient(135deg, #FFD700, #FFA500); }
.rank-2 { background: linear-gradient(135deg, #B0BEC5, #90A4AE); }
.rank-3 { background: linear-gradient(135deg, #CD7F32, #B87333); }
.rank-4, .rank-5 { background: #e8eaed; color: #666; }
.section-details { flex: 1; min-width: 0; }
.section-title { font-size: 13px; font-weight: 600; color: #0a1628; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.section-handle { font-size: 11px; color: #999; margin-top: 2px; }
.section-metrics { display: flex; gap: 16px; align-items: center; }
.metric-box { text-align: right; }
.metric-box-value { font-size: 14px; font-weight: 700; color: #0a1628; font-variant-numeric: tabular-nums; }
.metric-box-label { font-size: 10px; color: #999; text-transform: uppercase; letter-spacing: 0.3px; font-weight: 600; margin-top: 1px; }
.metric-divider { width: 1px; height: 28px; background: #eceef2; }

/* Activity */
.activity-list { display: flex; flex-direction: column; gap: 8px; }
.activity-item { display: flex; align-items: center; gap: 12px; padding: 12px 14px; background: #fafbfc; border: 1px solid #eceef2; border-radius: 10px; }
.activity-icon { width: 30px; height: 30px; min-width: 30px; background: rgba(46,125,50,0.08); border-radius: 8px; display: flex; align-items: center; justify-content: center; }
.activity-content { flex: 1; min-width: 0; }
.activity-title { font-size: 13px; font-weight: 600; color: #0a1628; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.activity-time { font-size: 11px; color: #999; margin-top: 2px; }
.activity-amount { font-size: 14px; font-weight: 700; color: #2E7D32; font-variant-numeric: tabular-nums; white-space: nowrap; }

/* Loading skeletons */
.loading-state .skel { background: linear-gradient(90deg, #eceef2 25%, #f5f6f8 50%, #eceef2 75%); background-size: 200% 100%; animation: shimmer 1.5s infinite; border-radius: 8px; }
.skel-card { background: white; border: 1px solid #eceef2; border-radius: 16px; padding: 24px; }
@keyframes shimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }

@media (max-width: 1200px) {
  .grid-split { grid-template-columns: 1fr; }
  .grid-7-5 { grid-template-columns: 1fr; }
}

@media (max-width: 960px) {
  .metric-strip { flex-wrap: wrap; gap: 16px 0; padding: 16px 0; }
  .metric-item { flex: 1 1 50%; padding: 8px 16px; }
  .metric-border { border-right: none; }
  .section-metrics { gap: 10px; }
  .metric-divider { display: none; }
}
</style>
